import { Result } from '@nwwp/result'
import { CloseableError } from './errors'
import {
  CloseableRecord,
  closeableRecordSchema,
  CloseableServiceConfig,
  type UpdateLeadSignalsParams,
  type UpdateStatusParams,
} from './types'

/**
 * Thin API client for Closeable CRM (mid-tier lead outreach vendor).
 *
 * Closeable's API is a single save-record upsert: every write — creating a
 * record, updating it, or marking it do-not-contact — goes through the same
 * `POST /save-record` endpoint with a `{ model: "Contact", values: {...} }`
 * body. Create-vs-update is matched on `external_client_id`, `primary_phone`,
 * or `primary_email` (never phone/email in `external_client_id`). Partial writes
 * (e.g. `updateStatus` sending only `client_status` plus a few metadata keys)
 * append/merge into the existing Contact — they do not replace prior metadata.
 */
export class CloseableService {
  constructor(private readonly config: CloseableServiceConfig) {}

  public async saveRecord(record: CloseableRecord): Promise<Result<true, CloseableError>> {
    // Strict parse is the runtime guard: Closeable must never receive fields
    // outside the agreed contract (no segment / Score Tier data).
    const parsed = closeableRecordSchema.safeParse(record)
    if (!parsed.success) {
      return Result.failure(
        new CloseableError('INVALID_RECORD', 'Record does not match the Closeable save-record contract', {
          cause: parsed.error,
        })
      )
    }

    return this.postSaveRecord(toContactValues(parsed.data))
  }

  /**
   * Pushes the lead's lifecycle status (the Opportunity's `StageName`) to
   * Closeable as `client_status`, via the same save-record upsert. Closeable
   * uses this to track the lead post-conversion and decides on its own side
   * when to stop outreach — replacing the earlier binary do-not-contact
   * stand-down. `clientStatus` is forwarded verbatim. Optional metadata
   * (`opportunityId`, `contactId`, `meetingScheduledAt`, `lossReason`, `ownerId`,
   * `ownerName`, `ownerEmail`, `firstMeetingDate`) rides the Contact metadata array;
   * advisor fields from the initial `saveRecord` are preserved because Closeable's
   * save-record upsert merges metadata by key (confirmed with the vendor).
   * `firstMeetingDate` (`first_meeting_date`) and `meetingScheduledAt`
   * (`meeting_scheduled_at`) are distinct keys. Null/absent/blank Salesforce IDs,
   * meeting datetime, owner fields and first meeting date are omitted — they do not
   * clear prior metadata. `lossReason` is different: `null` / `""` send
   * `{ key: 'loss_reason', value: null }` so Closeable can clear a prior value
   * (stage-confirmed, NWA-4204); `undefined` (field absent on the Opportunity
   * payload) omits the key and leaves Closeable alone.
   */
  public async updateStatus(params: UpdateStatusParams): Promise<Result<true, CloseableError>> {
    const { upsertKeys, clientStatus } = params
    const metadata = metadataFromUpdateStatusParams(params)

    const values: Record<string, unknown> = {
      ...upsertKeys,
      client_status: clientStatus,
    }
    if (metadata.length > 0) {
      values.metadata = metadata
    }

    return this.postSaveRecord(values)
  }

  /**
   * Keeps an already-sent Contact's Lead signals (`lead_score`, `bad_contact`) in
   * sync via a partial save-record — metadata only, no `client_status` and no
   * top-level Contact fields, so nothing else on the Contact is touched. Clear
   * semantics per `UpdateLeadSignalsParams`. Nothing to send → no request.
   */
  public async updateLeadSignals(params: UpdateLeadSignalsParams): Promise<Result<true, CloseableError>> {
    const { upsertKeys, leadScore, badContact } = params
    const metadata: Array<{ key: string; value: string | null }> = []
    if (leadScore !== undefined) {
      metadata.push({ key: 'lead_score', value: leadScore === null ? null : String(leadScore) })
    }
    const badContactValue = badContactMetadataValue(badContact)
    if (badContactValue !== undefined) {
      metadata.push({ key: 'bad_contact', value: badContactValue })
    }
    if (metadata.length === 0) {
      return Result.success(true)
    }

    return this.postSaveRecord({ ...upsertKeys, metadata })
  }

  private async postSaveRecord(values: Record<string, unknown>): Promise<Result<true, CloseableError>> {
    let response: Response

    try {
      response = await fetch(`${this.config.baseUrl}/save-record`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.config.apiToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'Contact',
          values: { ...values, client_product: this.config.clientProduct },
        }),
      })
    } catch (error) {
      return Result.failure(new CloseableError('NETWORK_ERROR', 'Failed to reach Closeable', { cause: error }))
    }

    if (!response.ok) {
      const responseBody = await response.text().catch(() => null)

      return Result.failure(
        new CloseableError('SAVE_RECORD_FAILED', `Closeable save-record failed with status ${response.status}`, {
          statusCode: response.status,
          responseBody,
        })
      )
    }

    return Result.success(true)
  }
}

/** Param field → Closeable Contact metadata key for `updateStatus` partial upserts. */
const UPDATE_STATUS_METADATA_KEYS = {
  opportunityId: 'salesforce_opportunity_id',
  contactId: 'salesforce_contact_id',
  meetingScheduledAt: 'meeting_scheduled_at',
  lossReason: 'loss_reason',
  ownerId: 'opportunity_owner_id',
  ownerName: 'opportunity_owner_name',
  ownerEmail: 'opportunity_owner_email',
  firstMeetingDate: 'first_meeting_date',
} as const satisfies Record<Exclude<keyof UpdateStatusParams, 'upsertKeys' | 'clientStatus'>, string>

function metadataFromUpdateStatusParams(params: UpdateStatusParams): Array<{ key: string; value: string | null }> {
  const entries: Array<{ key: string; value: string | null }> = []
  for (const field of Object.keys(UPDATE_STATUS_METADATA_KEYS) as Array<keyof typeof UPDATE_STATUS_METADATA_KEYS>) {
    const value = params[field]
    if (field === 'lossReason') {
      // null/"" → clear; undefined (prop absent on Opp) → omit; string → forward.
      if (value === undefined) {
        continue
      }
      entries.push({ key: UPDATE_STATUS_METADATA_KEYS.lossReason, value: value ? String(value) : null })
      continue
    }
    if (!value) {
      continue
    }
    entries.push({ key: UPDATE_STATUS_METADATA_KEYS[field], value: String(value) })
  }
  return entries
}

const METADATA_KEYS = [
  'investable_assets_range',
  'annual_income',
  'age',
  'zip',
  'salesforce_lead_id',
  'salesforce_opportunity_id',
  'city',
  'street',
  'assets',
  'cash',
  'current_retirement',
  'former_retirement',
  'investments',
  'stocks',
  'marital_status',
  'employment_status',
  'has_financial_advisor',
  'planning_to_move_on_retirement',
  'financial_focus',
  'retirement_scenario',
  'investor_comfort_level',
  'description',
  'lead_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'lead_magnet_slug',
  'lead_score',
  'bad_contact',
] as const satisfies ReadonlyArray<keyof CloseableRecord>

/** Closeable `bad_contact` metadata: null/false → `"false"`; true → `"true"`; undefined → omit. */
function badContactMetadataValue(badContact: boolean | null | undefined): string | undefined {
  if (badContact === undefined) {
    return undefined
  }
  return String(badContact === true)
}

function metadataEntriesFromRecord(record: Partial<CloseableRecord>): Array<{ key: string; value: string }> {
  const entries: Array<{ key: string; value: string }> = []
  for (const key of METADATA_KEYS) {
    const value = record[key]
    if (key === 'bad_contact') {
      const badContactValue = badContactMetadataValue(value as boolean | null | undefined)
      if (badContactValue !== undefined) {
        entries.push({ key, value: badContactValue })
      }
      continue
    }
    if (value === null || value === undefined) {
      continue
    }
    entries.push({ key, value: String(value) })
  }
  return entries
}

/**
 * Maps our internal record to Closeable's Contact `values`. Standard Contact
 * fields stay top-level under Closeable's own key names (`primary_email` /
 * `primary_phone`); qualification context has no fixed Contact field, so it
 * rides the `metadata` key/value array — Closeable's mechanism for non-standard
 * fields. Null fields are omitted rather than sent as empty metadata entries.
 */
function toContactValues(record: CloseableRecord): Record<string, unknown> {
  const values: Record<string, unknown> = {
    first_name: record.first_name,
    last_name: record.last_name,
    us_state: record.state,
    metadata: metadataEntriesFromRecord(record),
  }
  // Initial create: populate all Contact identity fields we have (match priority is
  // External Id → phone → email on Closeable's side). Closeable accepts null
  // external_client_id when upsert identity is phone/email. Partial upserts pass upsertKeys only.
  if (record.external_client_id !== undefined) {
    const trimmed = record.external_client_id?.trim()
    values.external_client_id = trimmed ? trimmed : null
  }
  if (record.phone != null && record.phone !== '') {
    values.primary_phone = record.phone
  }
  if (record.email != null && record.email !== '') {
    values.primary_email = record.email
  }
  return values
}
